import { useEffect, useState, type JSX } from 'react';

import { PlusCircleOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Col, Row } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { getACHRelationships } from 'src/actions';
import { cancelDeposit, cancelWithdraw, doClearCancelDeposit, doClearCancelWithdraw } from 'src/actions/cashiering';
import { DateConstant, FilterBarConstant } from 'src/constants';
import { useSubmittedAccountSelector, useTransfers } from 'src/hooks';
import {
  CancelTransferButton,
  ConfirmActionModal,
  FilterBar,
  MTable,
  TransferDetailsModal,
  TransferStatusTag,
  TransferTypeCol,
  NewAchRelationshipModal,
  MButton,
  MTooltip,
  ClientServicesLink,
  MSpinner,
  MTypography,
  CurrencyField,
  ViewDetailsButton,
} from 'src/lib';
import { MyAccountLayout } from 'src/lib/Layout/MyAccountLayout/MyAccountLayout';
import { MyAccountSidebarOtherMenuItemKey } from 'src/lib/Layout/MyAccountLayout/MyAccountSidebar';
import { Transfer, TransferStatus, TransferStatusLabel } from 'src/models';
import { Color, ScreenBreakpoint, Spacing } from 'src/styles';
import { getTransferStatusTagColor, mapFilterToGetTransferListQueryParamsDto } from 'src/utils';

import AchRelationships from './components/AchRelationships/AchRelationships';
import { DepositByCheckInstructions } from './components/DepositByCheckInstructions/DepositByCheckInstructions';
import { DepositByWireInstructions } from './components/DepositByWireInstructions/DepositByWireInstructions';
import { EditNickNameModal } from './components/EditNickname/EditNickNameModal';
import { NewDepositModal } from './components/NewDepositModal/NewDepositModal';
import { NewWithdrawalModal } from './components/NewWithdrawalModal/NewWithdrawalModal';
import * as Styles from './NewTransfer.styles';
import { RetirementAccountFunding } from './RetirementAccountFunding';

const ACTION_COLUMN_MIN_WIDTH = 112;

const fundingNoteSpacing = css`
  && {
    ${Spacing.mt16}
    ${Spacing.mb16}
  }
`;

const ugmaNoteSpacing = css`
  && {
    ${Spacing.mt16}
    margin-bottom: 0 !important;
  }
`;

const linkBankAccountButton = css`
  &.ant-btn {
    ${Spacing.px24}
  }
`;

export interface TransferViewModel {
  key: React.Key;
  date: string;
  type: JSX.Element;
  amount: JSX.Element;
  status: TransferStatus;
  cancel: JSX.Element | null;
  view: JSX.Element;
}

const defaultValues: FilterBarConstant.Sample = {
  sortBy: FilterBarConstant.SortBy.Newest,
  timeline: FilterBarConstant.FixedTimeline.ThisYear,
  customTimeline: undefined,
  filterBy: [],
  show: [],
};

export const BankAccountsAndFunding = (): JSX.Element => {
  const location: any = useLocation();
  const navigate = useNavigate();
  const achRelationships = useSelector((state: any) => state.cashiering.achRelationships.data);
  const loadingAchRelationships: boolean = useSelector((state: any) => state.cashiering.achRelationships.isLoading);

  const isCancelDepositLoading = useSelector((state: any) => state.cashiering.cancelDeposit.isLoading);
  const cancelDepositError = useSelector((state: any) => state.cashiering.cancelDeposit.error);
  const cancelDepositFailed = useSelector((state: any) => Boolean(state.cashiering.cancelDeposit.__failed));
  const cancelDepositSuccess = useSelector((state: any) => Boolean(state.cashiering.cancelDeposit.__succeeded));
  const isCancelWithdrawLoading = useSelector((state: any) => state.cashiering.cancelWithdraw.isLoading);
  const cancelWithdrawError = useSelector((state: any) => state.cashiering.cancelWithdraw.error);
  const cancelWithdrawFailed = useSelector((state: any) => Boolean(state.cashiering.cancelWithdraw.__failed));
  const cancelWithdrawSuccess = useSelector((state: any) => Boolean(state.cashiering.cancelWithdraw.__succeeded));

  const [selectedBankLink, setSelectedBankLink] = useState<any>({});
  const [isNewAchRealtionshipModalOpen, setIsNewAchRealtionshipModalOpen] = useState<boolean>(false);

  const [isEditNickNameModalOpen, setIsEditNickNameModalOpen] = useState<boolean>(false);

  const [filters, setFilters] = useState<FilterBarConstant.Sample>(defaultValues);

  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [isNewDepositModalOpen, setIsNewDepositModalOpen] = useState<boolean>(false);
  const [isNewWithdrawalModalOpen, setIsNewWithdrawalModalOpen] = useState<boolean>(false);
  const [isCancelConfirmationModalOpen, setIsCancelConfirmationModalOpen] = useState<boolean>(false);
  const [isViewTransferDetailsModalOpen, setIsViewTransferDetailsModalOpen] = useState<boolean>(false);

  const { account } = useSubmittedAccountSelector();
  const { transferList, isTransferListLoading } = useTransfers({
    queryParams: mapFilterToGetTransferListQueryParamsDto(filters),
    dependency: [filters.timeline, filters.customTimeline, filters.filterBy, filters.sortBy, account.accountId],
    refetchOnChange: true,
  });
  const isMobile = useMediaQuery({ query: `(max-width: ${ScreenBreakpoint.mobile.max})` });

  const dispatch = useDispatch();

  const canAddAchRelationship = () => achRelationships?.length < 2;

  const onOpenCancelTransferModal = (transfer: Transfer) => {
    setSelectedTransfer(transfer);
    setIsCancelConfirmationModalOpen(true);
  };

  const onOpenTransferDetailsModal = (transfer: Transfer) => {
    setSelectedTransfer(transfer);
    setIsViewTransferDetailsModalOpen(true);
  };

  const onDeposit = () => {
    setIsNewDepositModalOpen(true);
  };

  const onWithdraw = () => {
    setIsNewWithdrawalModalOpen(true);
  };

  const onEdit = () => {
    setIsEditNickNameModalOpen(true);
  };

  const onConfirmCancelTransfer = (selectedItem: Transfer) => {
    if (selectedItem.direction.isDeposit) {
      dispatch(cancelDeposit(selectedItem.id, account.accountId));

      return;
    }
    dispatch(cancelWithdraw(selectedItem.id, account.accountId));
  };

  const onApproveFilters = (newFilters: FilterBarConstant.Sample) => {
    setFilters({ ...newFilters });
  };

  const transferListCols: ColumnsType<Omit<TransferViewModel, 'status'>> = [
    {
      title: 'Date',
      width: '20%',
      render: (value: Transfer) => moment(value.createdAt).format(DateConstant.DEFAULT_DATETIME_FORMAT),
    },
    {
      title: 'Type',
      width: '35%',
      render: (value: Transfer) => <TransferTypeCol value={value} testId='column-type-id' />,
    },
    {
      title: 'Amount',
      width: '15%',
      align: 'right',
      render: (value: Transfer) => (
        <CurrencyField
          value={value.direction.isWithdrawal ? -value.amount : value.amount}
          className={Styles.amountValue({ isNegative: value.direction.isWithdrawal })}
        />
      ),
    },
    {
      title: 'Action',
      key: 'cancel',
      width: 1,
      onCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
      onHeaderCell: () => ({ style: { minWidth: ACTION_COLUMN_MIN_WIDTH } }),
      render: (value: Transfer) => <CancelTransferButton value={value} onClick={onOpenCancelTransferModal} />,
    },
    {
      key: 'view',
      width: 96,
      render: (value: Transfer) => (
        <ViewDetailsButton
          onClick={() => onOpenTransferDetailsModal(value)}
          testId={`transfer-${value.id}-view-details`}
        />
      ),
    },
  ];

  const mapStatusLabelListToByFilterGroup = (): FilterBarConstant.GroupOption[] => {
    const options = Object.values(TransferStatusLabel).map(value => ({
      label: <TransferStatusTag value={value} />,
      value: `${value}_${FilterBarConstant.StatusCategory.MoneyTransfer}`,
      tag: {
        text: value,
        ...getTransferStatusTagColor(value),
      },
    }));

    return [{ title: 'Money Transaction(s)', options }];
  };

  useEffect(() => {
    if (account?.type.isIndividual || account?.type.isJoint || account?.type.isEntity) {
      dispatch(getACHRelationships(account?.accountId));
    }
  }, []);

  return (
    <MyAccountLayout
      sidebarMenuItemKey={MyAccountSidebarOtherMenuItemKey.MoneyTransfers}
      title='Bank Accounts & Funding'
      backlink={
        location?.state?.offerId && location?.state?.path
          ? { to: `/${location?.state?.path}/${location?.state?.offerId}`, label: 'Back to Offer' }
          : undefined
      }
      testId={'bank-account-funding'}>
      {loadingAchRelationships ? (
        <MSpinner />
      ) : (
        <>
          {account?.type.isRetirement || account?.type.isUgma ? null : (
            <Col span={24}>
              <Row className={Spacing.my16}>
                <Col span={24}>
                  <MTypography variant='h5' weight='semibold' color={Color.Brand6}>
                    Linked Bank Accounts
                  </MTypography>
                </Col>
              </Row>

              <AchRelationships
                achRelationships={achRelationships}
                selectedBankLink={selectedBankLink}
                setSelectedBankLink={setSelectedBankLink}
                loadingAchRelationships={loadingAchRelationships}
                onDeposit={onDeposit}
                onWithdraw={onWithdraw}
                onEdit={onEdit}
                testId={'link-bank-accounts'}
              />

              {canAddAchRelationship() && (
                <MButton
                  onClick={() => {
                    setIsNewAchRealtionshipModalOpen(true);
                  }}
                  className={cx(Spacing.my16, linkBankAccountButton)}
                  testId={'money-transfers-btn-link-bank-account'}>
                  <PlusCircleOutlined style={{ fontSize: 16, marginRight: 4 }} />
                  Link a new bank account
                </MButton>
              )}
            </Col>
          )}

          {account?.type.isRetirement ? (
            <RetirementAccountFunding />
          ) : (
            <>
              {account?.type.isEntity && !account?.isEntityAccountTypeActive ? (
                <Col span={24}>
                  <MTypography variant='subtitle1' color={Color.Gray8} className={fundingNoteSpacing}>
                    You will not be able to deposit funds by wire/check until your account is established. You can find
                    more information at{' '}
                    <a onClick={() => (account?.id ? navigate(`/accounts/${account?.id}/account-status`) : null)}>
                      Account Status
                    </a>
                    {'.'}
                  </MTypography>
                </Col>
              ) : (
                <>
                  {account?.type.isUgma && (
                    <Col span={24}>
                      <MTypography variant='subtitle1' color={Color.Gray8} className={ugmaNoteSpacing}>
                        Please select one of the following to find more information regarding depositing funds to your
                        account.
                      </MTypography>
                    </Col>
                  )}
                  <Col span={24}>
                    <MTooltip
                      type='modal'
                      title='Deposit Funds By Wire'
                      label='Deposit Funds By Wire'
                      icon='info'
                      showIcon>
                      <DepositByWireInstructions />
                    </MTooltip>
                  </Col>
                  <Col span={24}>
                    <MTooltip
                      type='modal'
                      title='Deposit Funds By Check'
                      label='Deposit Funds By Check'
                      icon='info'
                      showIcon>
                      <DepositByCheckInstructions />
                    </MTooltip>
                  </Col>
                </>
              )}

              <Col span={24}>
                <MTypography variant='subtitle1' color={Color.Gray8} className={fundingNoteSpacing}>
                  If you need to withdraw funds by wire or check please contact <ClientServicesLink />.
                </MTypography>
              </Col>
            </>
          )}

          <Row align='middle' className={Spacing.my16}>
            <Col xs={18} sm={18} md={24}>
              <MTypography variant='h5' weight='semibold' color={Color.Brand6}>
                Deposits & Withdrawals
              </MTypography>
            </Col>
            <Col xs={6} sm={6} md={24}>
              <FilterBar
                sortByOptions={[
                  { label: FilterBarConstant.SortByLabel.Newest, value: FilterBarConstant.SortBy.Newest },
                  { label: FilterBarConstant.SortByLabel.Oldest, value: FilterBarConstant.SortBy.Oldest },
                ]}
                byFilter={mapStatusLabelListToByFilterGroup()}
                onApprove={onApproveFilters}
                defaultValue={defaultValues}
                initValue={filters}
              />
            </Col>
          </Row>
          <Row>
            <Col span={24} className={Styles.tableContainer}>
              <MTable
                loading={isTransferListLoading}
                columns={transferListCols}
                data={transferList}
                pagination={{ position: ['bottomRight'] }}
                scroll={isMobile ? { x: 180 } : {}}
                testId={'items-table'}
              />
            </Col>
          </Row>

          <NewAchRelationshipModal
            open={isNewAchRealtionshipModalOpen}
            onClose={() => setIsNewAchRealtionshipModalOpen(false)}
            onExit={() => setIsNewAchRealtionshipModalOpen(false)}
            onFinish={() => setIsNewAchRealtionshipModalOpen(false)}
          />

          {isEditNickNameModalOpen && (
            <EditNickNameModal
              achRelationship={selectedBankLink}
              open={isEditNickNameModalOpen}
              onClose={() => setIsEditNickNameModalOpen(false)}
            />
          )}

          {isNewDepositModalOpen && (
            <NewDepositModal
              isOpen={isNewDepositModalOpen}
              onClose={() => setIsNewDepositModalOpen(false)}
              selectedBankAccount={selectedBankLink}
            />
          )}

          {isNewWithdrawalModalOpen && (
            <NewWithdrawalModal
              isOpen={isNewWithdrawalModalOpen}
              onClose={() => setIsNewWithdrawalModalOpen(false)}
              selectedBankAccount={selectedBankLink}
            />
          )}

          {selectedTransfer && (
            <TransferDetailsModal
              value={selectedTransfer}
              isOpen={isViewTransferDetailsModalOpen}
              onClose={() => {
                setIsViewTransferDetailsModalOpen(false);
                setSelectedTransfer(null);
              }}
            />
          )}

          {selectedTransfer && (
            <ConfirmActionModal
              title='Cancel Confirmation'
              message={`Are you sure you want to cancel this ${
                selectedTransfer.direction.isDeposit ? 'deposit' : 'withdrawal'
              } transfer?`}
              successMessage={`${
                selectedTransfer.direction.isDeposit ? 'Deposit' : 'Withdrawal'
              } Transfer was successfully cancelled.`}
              errorMessage={
                selectedTransfer.direction.isDeposit ? cancelDepositError?.message : cancelWithdrawError?.message
              }
              isOpen={isCancelConfirmationModalOpen}
              isError={selectedTransfer.direction.isDeposit ? cancelDepositFailed : cancelWithdrawFailed}
              isSuccess={selectedTransfer.direction.isDeposit ? cancelDepositSuccess : cancelWithdrawSuccess}
              isLoading={selectedTransfer.direction.isDeposit ? isCancelDepositLoading : isCancelWithdrawLoading}
              onCancel={() => {
                setIsCancelConfirmationModalOpen(false);
                setSelectedTransfer(null);
              }}
              onOk={() => {
                onConfirmCancelTransfer(selectedTransfer);
              }}
              onClose={() => {
                setIsCancelConfirmationModalOpen(false);
                setSelectedTransfer(null);

                if (selectedTransfer.direction.isDeposit) {
                  dispatch(doClearCancelDeposit());

                  return;
                }
                dispatch(doClearCancelWithdraw());
              }}
            />
          )}
        </>
      )}
    </MyAccountLayout>
  );
};
